import { useCallback } from 'react'

import { NavigateOptions, useNavigate } from 'react-router-dom'

import { useLogCashWebCdfEvent } from '@cash-web/shared-feature-analytics'
import { useJourneyContext } from '@cash-web/shared-journey-data-access-context'
import { JourneyScope } from '@cash-web/shared-journey-util-main'
import { getRouteInfo } from '@cash-web/shared-util-route-info'
import { WebViewUnsupportedFeature } from '@squareup/customer-data-events-web'

export type LoadUrlOptions = NavigateOptions & { scope?: Partial<JourneyScope> }
export type LoadUrlProps = { trackingName?: string; url?: string; options?: LoadUrlOptions; target?: string }

export type LoadUrlLocationState = Partial<{
  clientRouteReferrerPath: string
}>

export const useSafeHook =
  <T extends (...args: unknown[]) => unknown>(hook: T) =>
  (...args: Parameters<T>) => {
    try {
      return hook(...args) as ReturnType<T>
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (error) {
      return undefined
    }
  }

// Create a safe version of useNavigate that doesn't throw during SSR
export const useSafeNavigate = () => {
  const navigate = useSafeHook(useNavigate)()
  if (navigate == null) {
    return (path: string, options?: NavigateOptions) => {
      window.open(path)
    }
  }
  return navigate
}

export const useLoadUrl = () => {
  const navigate = useSafeNavigate()
  const journey = useJourneyContext()
  const logCdfEvent = useLogCashWebCdfEvent()

  const loadUrl = useCallback(
    async ({ url, options, target = '_blank' }: LoadUrlProps) => {
      const pathname = url?.replace('https://internal.cash.app', '')

      let routeInfo = getRouteInfo(url)
      const routeInfoState: LoadUrlLocationState = {
        clientRouteReferrerPath: pathname,
      }

      if (routeInfo == null) {
        return { success: false }
      }

      // Occasionally the internal route definitions will contain a route that is also backed by a journey.
      // The common case for this is the support app performing an external navigation to the account app.
      // To avoid this, any internal navigations that are also a routable journey, will be converted to a journey instead.
      const isInternalRouteWithFallback = routeInfo.type === 'internal' && routeInfo.journeyFallback

      // Even if there's an unsupported route in the route config, there may still be a journey that is available.
      const isUnsupported = routeInfo.type === 'known-unsupported' || routeInfo.type === 'unsupported'

      if ((isInternalRouteWithFallback || isUnsupported) && pathname && journey.matches(pathname)) {
        routeInfo = {
          type: 'journey',
          journey: pathname,
        }
      }

      switch (routeInfo?.type) {
        case 'external': {
          routeInfo.redirect(target)
          return { success: true }
        }
        case 'internal': {
          if (routeInfo.url != null && navigate) {
            journey.cancelAll({ excludeTargets: ['outlet:moneybot', 'outlet:moneybot-action'] })
            navigate?.(routeInfo.url, {
              ...options,
              state: { ...options?.state, ...routeInfoState },
            })
          } else {
            routeInfo.redirect()
          }
          return { success: true }
        }
        case 'journey': {
          if (journey) {
            await journey.navigate(routeInfo.journey, {
              ...options,
              replace: options?.replace != null ? options?.replace : routeInfo.replace,
              scope: { ...routeInfo.scope, ...options?.scope },
              state: { ...options?.state, ...routeInfo.state, ...routeInfoState },
            })
          }
          return { success: true }
        }
        case 'unsupported':
        case 'known-unsupported': {
          logCdfEvent(
            WebViewUnsupportedFeature({
              source: 'useLoadUrl',
              feature: 'CLIENT_ROUTE',
              action: routeInfo.type,
              context: url,
            })
          )
          return { success: false }
        }
        default: {
          return { success: false }
        }
      }
    },
    [journey, navigate, logCdfEvent]
  )
  return loadUrl
}
